/**
 * A long list drawn a few at a time, more as the reader scrolls (2026-10-05).
 *
 * Dev grew to 173 raves and the list drew every card at once — 122 under one
 * filter, each with its cover, logo and countdown. The first `step` are drawn;
 * when the sentinel after them comes within `margin` of the screen, `step`
 * more. A button does the same for a keyboard, a screen reader, and anywhere
 * without `IntersectionObserver`. Changing what is listed (`resetKey`) starts
 * again from the top — a new filter is a new list.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function useIncremental<T>(items: readonly T[], resetKey: string, step = 24, margin = '600px') {
  const [count, setCount] = useState(step);
  useEffect(() => { setCount(step); }, [resetKey, step]);

  const more = useCallback(() => setCount((c) => c + step), [step]);
  const hasMore = count < items.length;

  const observer = useRef<IntersectionObserver | null>(null);
  /** Callback ref for the element after the list. */
  const sentinel = useCallback((node: Element | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    observer.current = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) more(); },
      { rootMargin: margin },
    );
    observer.current.observe(node);
  }, [more, margin]);
  useEffect(() => () => observer.current?.disconnect(), []);

  return { visible: items.slice(0, count), hasMore, remaining: Math.max(0, items.length - count), more, sentinel };
}
